// Typed digital fact-find API client for the web layer (PRD #70).
//
// Wraps the `factFindContract` endpoints via the shared `api` client and
// exposes react-query hooks. Mutations invalidate `factFindKeys.all` so the
// register and the desk review view re-render together.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch } from '@/lib/api';
import { track } from '@/lib/analytics/client';
import type {
    CreateFactFindRequest,
    FactFind,
    FactFindAnswer,
    FactFindClientOption,
    FactFindDocument,
    FactFindRegisterRow,
    FactFindRiskSummaryRow,
    FactFindSigningState,
    FactFindStatus,
    PrefillSuggestion,
    SendForSignatureSigner,
} from '@septima/contract';

export const factFindKeys = {
    all: ['fact-find'] as const,
    register: (imId?: string) => ['fact-find', 'register', imId ?? null] as const,
    riskSummary: (imId?: string) => ['fact-find', 'risk-summary', imId ?? null] as const,
    clientOptions: () => ['fact-find', 'client-options'] as const,
    detail: (clientId: string) => ['fact-find', 'detail', clientId] as const,
    prefill: (clientId: string) => ['fact-find', 'prefill', clientId] as const,
    document: (clientId: string) => ['fact-find', 'document', clientId] as const,
    signing: (clientId: string) => ['fact-find', 'signing', clientId] as const,
};

function errMsg(body: unknown, status: number): string {
    return (body as { error?: string })?.error ?? `Request failed (${status})`;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useFactFindRegister(imId?: string) {
    return useQuery({
        queryKey: factFindKeys.register(imId),
        queryFn: async (): Promise<FactFindRegisterRow[]> => {
            const res = await api.factFind.listRegister({ query: imId ? { imId } : {} });
            if (res.status !== 200) throw new Error(`Register failed: ${res.status}`);
            return res.body.rows;
        },
        staleTime: 10_000,
    });
}

/**
 * Per-client fact-find risk summary (band / mandate / total + capacity flags)
 * for the Vulnerable Clients surface. Sourced from the `desk`-readable
 * FactFind, so it works on the Desk Vulnerable Clients page without reaching
 * into the `comply`-only VulnerabilityRecord.
 */
export function useFactFindRiskSummary(imId?: string) {
    return useQuery({
        queryKey: factFindKeys.riskSummary(imId),
        queryFn: async (): Promise<FactFindRiskSummaryRow[]> => {
            const res = await api.factFind.riskSummary({ query: imId ? { imId } : {} });
            if (res.status !== 200) throw new Error(`Risk summary failed: ${res.status}`);
            return res.body.rows;
        },
        staleTime: 10_000,
    });
}

/** Clients for the manual-create typeahead (identity + has-fact-find flag). */
export function useFactFindClientOptions() {
    return useQuery({
        queryKey: factFindKeys.clientOptions(),
        queryFn: async (): Promise<FactFindClientOption[]> => {
            const res = await api.factFind.listClientOptions({ query: {} });
            if (res.status !== 200) throw new Error(`Clients failed: ${res.status}`);
            return res.body.clients;
        },
        staleTime: 30_000,
    });
}

export function useFactFind(clientId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.detail(clientId ?? ''),
        enabled: Boolean(clientId),
        queryFn: async (): Promise<FactFind> => {
            const res = await api.factFind.getFactFind({
                params: { clientId: clientId! },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        staleTime: 5_000,
    });
}

export function useFactFindPrefill(clientId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.prefill(clientId ?? ''),
        enabled: Boolean(clientId),
        queryFn: async (): Promise<PrefillSuggestion[]> => {
            const res = await api.factFind.listPrefill({
                params: { clientId: clientId! },
            });
            if (res.status !== 200) throw new Error(`Prefill failed: ${res.status}`);
            return res.body.suggestions;
        },
        staleTime: 5_000,
    });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateFactFindDraft() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            by: string;
            reason?: string;
            refreshSections?: string[];
        }) => {
            const res = await api.factFind.createDraft({
                params: { clientId: input.clientId },
                body: {
                    by: input.by,
                    reason: input.reason ?? 'Initial fact find',
                    refreshSections: input.refreshSections ?? [],
                },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

/**
 * Manual create (PRD #70 Slice 3): start a fact find for an existing client by
 * id, or "from scratch" by supplying a new client name. The `clientType`
 * selects the canonical Septima form (the active dialog tab). Returns the
 * resolved `clientId` so the caller can navigate to the blank form.
 */
export function useCreateFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (
            input: CreateFactFindRequest,
        ): Promise<{ clientId: string; factFind: FactFind }> => {
            const res = await api.factFind.createFactFind({ body: input });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return { clientId: res.body.clientId, factFind: res.body.factFind };
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useSaveFactFindSection() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            sectionId: string;
            answers: Record<string, FactFindAnswer>;
            by: string;
            actor: 'client' | 'im';
        }) => {
            const res = await api.factFind.saveSection({
                params: { clientId: input.clientId },
                body: {
                    by: input.by,
                    actor: input.actor,
                    sectionId: input.sectionId,
                    answers: input.answers,
                },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useSaveAtr() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            responses: Record<string, string>;
            by: string;
            actor: 'client' | 'im';
        }) => {
            const res = await api.factFind.saveAtr({
                params: { clientId: input.clientId },
                body: { by: input.by, actor: input.actor, responses: input.responses },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useSaveCfl() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            by: string;
            actor: 'client' | 'im';
            annualIncome: number;
            essentialExpenditure: number;
            liquidAssets: number;
            knownCommitments: number;
            portfolioValue: number;
        }) => {
            const res = await api.factFind.saveCfl({
                params: { clientId: input.clientId },
                body: {
                    by: input.by,
                    actor: input.actor,
                    annualIncome: input.annualIncome,
                    essentialExpenditure: input.essentialExpenditure,
                    liquidAssets: input.liquidAssets,
                    knownCommitments: input.knownCommitments,
                    portfolioValue: input.portfolioValue,
                },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useTransitionFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            to: FactFindStatus;
            by: string;
            note?: string;
        }) => {
            const res = await api.factFind.transition({
                params: { clientId: input.clientId },
                body: { to: input.to, by: input.by, note: input.note },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useAddFactFindAnnotation() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            sectionId: string;
            note: string;
            kind: 'note' | 'gap' | 'inconsistency';
            by: string;
        }) => {
            const res = await api.factFind.addAnnotation({
                params: { clientId: input.clientId },
                body: {
                    by: input.by,
                    sectionId: input.sectionId,
                    note: input.note,
                    kind: input.kind,
                },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.factFind;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useRefreshFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            sections: string[];
            message: string;
            by: string;
        }) => {
            const res = await api.factFind.refresh({
                params: { clientId: input.clientId },
                body: {
                    by: input.by,
                    sections: input.sections,
                    message: input.message,
                },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useAcceptPrefill() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { suggestionId: string; by: string }) => {
            const res = await api.factFind.acceptPrefill({
                params: { suggestionId: input.suggestionId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

export function useRejectPrefill() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { suggestionId: string; by: string }) => {
            const res = await api.factFind.rejectPrefill({
                params: { suggestionId: input.suggestionId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body;
        },
        onSuccess: () => void qc.invalidateQueries({ queryKey: factFindKeys.all }),
    });
}

// ---------------------------------------------------------------------------
// Generated document (PRD #70 Slice 7)
// ---------------------------------------------------------------------------

/** The generated fact-find document meta (no PDF bytes), or null if none yet. */
export function useFactFindDocument(clientId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.document(clientId ?? ''),
        enabled: Boolean(clientId),
        queryFn: async (): Promise<FactFindDocument | null> => {
            const res = await api.factFind.getDocument({ params: { clientId: clientId! } });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.document;
        },
        staleTime: 5_000,
    });
}

/** Generate / regenerate the filled fact-find PDF; returns the document meta. */
export function useGenerateFactFindDocument() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { clientId: string; by: string }) => {
            const res = await api.factFind.generateDocument({
                params: { clientId: input.clientId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.document;
        },
        onSuccess: (_doc, input) =>
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.clientId) }),
    });
}

/**
 * Download the filled PDF: fetch the binary route (credentials included) and
 * trigger a browser download. Returns once the download has been kicked off.
 * The binary route is not part of the ts-rest client, so this uses `apiFetch`.
 */
export async function downloadFilledFactFindPdf(
    clientId: string,
    clientName: string,
): Promise<void> {
    return downloadFactFindPdf('filled', clientId, clientName);
}

/** Download the signed PDF (DocuSign combined doc or manually-uploaded copy). */
export async function downloadSignedFactFindPdf(
    clientId: string,
    clientName: string,
): Promise<void> {
    return downloadFactFindPdf('signed', clientId, clientName);
}

/** Shared binary-PDF download for the filled / signed routes. */
async function downloadFactFindPdf(
    kind: 'filled' | 'signed',
    clientId: string,
    clientName: string,
): Promise<void> {
    track('interaction', {
        details: { action: 'export_pdf', document: `fact-find-${kind}` },
        objectType: 'fact-find',
        objectId: clientId,
    });
    const res = await apiFetch(`/api/fact-find/${encodeURIComponent(clientId)}/document/${kind}`);
    if (!res.ok) {
        throw new Error(
            res.status === 404 ? `No ${kind} PDF yet` : `Download failed (${res.status})`,
        );
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    try {
        const a = document.createElement('a');
        a.href = url;
        a.download = `fact-find-${kind === 'signed' ? 'signed-' : ''}${clientName || clientId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    } finally {
        URL.revokeObjectURL(url);
    }
}

// ---------------------------------------------------------------------------
// Signing (PRD #70 Slice 9) — DocuSign send + manual fallback
// ---------------------------------------------------------------------------

/** Signing state for the client fact-find view (gates the DocuSign affordance). */
export function useFactFindSigning(clientId: string | undefined) {
    return useQuery({
        queryKey: factFindKeys.signing(clientId ?? ''),
        enabled: Boolean(clientId),
        queryFn: async (): Promise<FactFindSigningState> => {
            const res = await api.factFind.getSigningState({ params: { clientId: clientId! } });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.signing;
        },
        staleTime: 5_000,
    });
}

/** Send the filled fact-find PDF for DocuSign signature. */
export function useSendForSignature() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: {
            clientId: string;
            by: string;
            signers: SendForSignatureSigner[];
            emailSubject?: string;
        }) => {
            const res = await api.factFind.sendForSignature({
                params: { clientId: input.clientId },
                body: { by: input.by, signers: input.signers, emailSubject: input.emailSubject },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.signing;
        },
        onSuccess: (_s, input) => {
            void qc.invalidateQueries({ queryKey: factFindKeys.signing(input.clientId) });
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.clientId) });
        },
    });
}

/** Mark the fact-find document signed (manual, without an uploaded copy). */
export function useMarkFactFindSigned() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { clientId: string; by: string }) => {
            const res = await api.factFind.markSigned({
                params: { clientId: input.clientId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body.signing;
        },
        onSuccess: (_s, input) => {
            void qc.invalidateQueries({ queryKey: factFindKeys.signing(input.clientId) });
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.clientId) });
        },
    });
}

/**
 * Upload a signed PDF (manual fallback). The multipart route is not part of the
 * ts-rest client, so this posts FormData via `apiFetch`.
 */
export function useUploadSignedFactFind() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: { clientId: string; file: File }) => {
            const form = new FormData();
            form.append('file', input.file);
            const res = await apiFetch(
                `/api/fact-find/${encodeURIComponent(input.clientId)}/document/upload-signed`,
                { method: 'POST', body: form },
            );
            if (!res.ok) {
                let message = `Upload failed (${res.status})`;
                try {
                    const body = (await res.json()) as { message?: string; error?: string };
                    message = body.message ?? body.error ?? message;
                } catch {
                    // keep the default message
                }
                throw new Error(message);
            }
            return (await res.json()) as { ok: true };
        },
        onSuccess: (_r, input) => {
            void qc.invalidateQueries({ queryKey: factFindKeys.signing(input.clientId) });
            void qc.invalidateQueries({ queryKey: factFindKeys.document(input.clientId) });
        },
    });
}

// ---------------------------------------------------------------------------
// Client-email draft (per-user advisory-draft path, Outlook)
// ---------------------------------------------------------------------------

interface MailboxStatus {
    configured: boolean;
    connected: boolean;
    email: string | null;
    connectedAt?: string | null;
}

/**
 * Connection status of the signed-in user's Outlook (Microsoft 365). Gates the
 * draft affordance — mirrors the Settings panel's query.
 */
export function useOutlookStatus() {
    return useQuery<MailboxStatus>({
        queryKey: ['integration', 'outlook', 'status'],
        queryFn: async () => {
            const res = await apiFetch('/api/outlook/status', { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return (await res.json()) as MailboxStatus;
        },
        staleTime: 30_000,
    });
}

/**
 * Draft a client cover-note email into the IM's Outlook mailbox. Returns the
 * draft id + a deep link so the caller can open it for review + sending. The
 * server defaults the mailbox provider to Outlook.
 */
export function useDraftClientEmail() {
    return useMutation({
        mutationFn: async (input: { clientId: string; by: string }) => {
            const res = await api.factFind.draftClientEmail({
                params: { clientId: input.clientId },
                body: { by: input.by },
            });
            if (res.status !== 200) throw new Error(errMsg(res.body, res.status));
            return res.body;
        },
    });
}
